import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import Navbar from './Navbar';
import Footer from './Footer';
import { useTranslation } from '../contexts/TranslationContext';
import api from '../utils/api';
import balanceGlobalData from '../data/balance_global.json';
import cautiousGlobalData from '../data/cautious_global.json';
import wealthGlobalData from '../data/wealth_global.json';
import tacticalGlobalData from '../data/tactical_global.json';
import shariahBalancedData from '../data/shariah_balanced.json';
import { DocumentArrowDownIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/24/outline';
import { Reveal, PageHero } from './LandingShared';
import { Helmet } from 'react-helmet-async';

// --- Reusable Components ---
const Section = ({ children }) => <div className="py-12 sm:py-16">{children}</div>;
const SectionTitle = ({ children }) => <h2 className="text-3xl font-bold tracking-tight text-center text-gray-900 sm:text-4xl">{children}</h2>;

// --- Portfolio Display Component ---
const PortfolioDisplay = ({ portfolioData, portfolioKey, factsheets, loadingFactsheets, t }) => {
    // State for expand/collapse of asset type sections
    const [expandedSections, setExpandedSections] = useState(() => {
        // Start with all sections expanded by default
        const initial = {};
        portfolioData.holdings.forEach(holding => {
            if (holding.assetType) {
                initial[holding.assetType] = true;
            }
        });
        return initial;
    });

    // Prepare asset exposure data for pie chart (filter out 0% values)
    const assetExposureData = Object.entries(portfolioData.assetExposure)
        .filter(([name, value]) => value > 0)
        .map(([name, value]) => ({
            name,
            value
        }));

    // Group holdings by asset type if assetType exists, otherwise sort by weight
    const hasAssetTypes = portfolioData.holdings.some(h => h.assetType);

    // Define asset type order
    const assetTypeOrder = ['Equity', 'Real Estate', 'Sukuk', 'Gold', 'Fixed Income', 'Cash', 'Commodity'];

    let groupedHoldings = {};
    let sortedHoldings = [];

    if (hasAssetTypes) {
        // Group by asset type, filtering out holdings with 0% weight
        portfolioData.holdings.forEach(holding => {
            // Skip holdings with 0% weight
            if (holding.weight === 0 || holding.weight === 0.0) return;

            const assetType = holding.assetType || 'Other';
            if (!groupedHoldings[assetType]) {
                groupedHoldings[assetType] = [];
            }
            groupedHoldings[assetType].push(holding);
        });

        // Sort each group by weight (highest to lowest)
        Object.keys(groupedHoldings).forEach(type => {
            groupedHoldings[type].sort((a, b) => b.weight - a.weight);
        });
    } else {
        // Fallback: sort all holdings by weight (highest to lowest), filtering out 0% weight
        sortedHoldings = [...portfolioData.holdings]
            .filter(holding => holding.weight > 0)
            .sort((a, b) => b.weight - a.weight);
    }

    // Toggle expand/collapse for asset type section
    const toggleSection = (assetType) => {
        setExpandedSections(prev => ({
            ...prev,
            [assetType]: !prev[assetType]
        }));
    };

    // Calculate total weight for each asset type
    const getAssetTypeTotal = (assetType) => {
        return groupedHoldings[assetType]?.reduce((sum, h) => sum + h.weight, 0) || 0;
    };

    // Colors for asset exposure chart
    const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#6366f1', '#ef4444'];

    return (
        <div className="mt-10 bg-white rounded-2xl shadow-sm border border-gray-100 p-6 sm:p-8">
            <h3 className="text-2xl font-bold text-gray-900 mb-6">
                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.title`)}
            </h3>

            {/* Descriptions */}
            <Reveal>
                <div className="prose prose-lg text-justify mb-8 space-y-4">
                    <p>{t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.description1`)}</p>
                    <p>{t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.description2`)}</p>
                    <p className="text-sm text-gray-600">{t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.description3`)}</p>
                </div>
            </Reveal>

            {/* Date */}
            <div className="mb-8 text-sm text-gray-600">
                <strong>{t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.asOf`)}:</strong> {portfolioData.date}
            </div>

            {/* Holdings and Asset Exposure Side-by-Side */}
            <Reveal delay={100}>
                <div className="mb-12">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        {/* Holdings Table - Left Side */}
                        <div>
                            <div className="overflow-x-auto">
                                {hasAssetTypes ? (
                                    // Grouped by asset type with expand/collapse
                                    <div className="space-y-2">
                                        {assetTypeOrder
                                            .filter(type => groupedHoldings[type] && groupedHoldings[type].length > 0)
                                            .map(assetType => {
                                                const isExpanded = expandedSections[assetType] !== false;
                                                const totalWeight = getAssetTypeTotal(assetType);
                                                const holdingCount = groupedHoldings[assetType].length;

                                                return (
                                                    <div key={assetType} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                                        <button
                                                            onClick={() => toggleSection(assetType)}
                                                            className={`w-full px-3 py-2 flex items-center justify-between transition-all duration-200 ${
                                                                isExpanded
                                                                    ? 'bg-gradient-to-r from-slate-50 to-blue-50/50'
                                                                    : 'bg-gray-50 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {isExpanded ? (
                                                                    <ChevronUpIcon className="h-4 w-4 text-blue-500" />
                                                                ) : (
                                                                    <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                                                )}
                                                                <span className="text-sm font-semibold text-gray-800">{assetType}</span>
                                                                <span className="text-xs text-gray-500">({holdingCount})</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700">{totalWeight.toFixed(1)}%</span>
                                                        </button>
                                                        {isExpanded && (
                                                            <div className="border-t border-gray-200">
                                                                <table className="min-w-full bg-white text-xs">
                                                                    <thead className="bg-gray-50/80">
                                                                        <tr>
                                                                            <th className="py-1.5 px-2.5 text-left font-medium text-gray-600 text-xs">
                                                                                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.holdings`)}
                                                                            </th>
                                                                            <th className="py-1.5 px-2.5 text-right font-medium text-gray-600 text-xs">
                                                                                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.weight`)}
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100">
                                                                        {groupedHoldings[assetType].map((holding, index) => (
                                                                            <tr key={index} className="hover:bg-blue-50/50 transition-colors duration-150">
                                                                                <td className="py-1.5 px-2.5 text-gray-800 text-xs">{holding.name}</td>
                                                                                <td className="py-1.5 px-2.5 text-right font-medium text-gray-700 text-xs">{holding.weight}%</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                        {/* Handle any asset types not in the predefined order */}
                                        {Object.keys(groupedHoldings)
                                            .filter(type => !assetTypeOrder.includes(type))
                                            .map(assetType => {
                                                const isExpanded = expandedSections[assetType] !== false;
                                                const totalWeight = getAssetTypeTotal(assetType);
                                                const holdingCount = groupedHoldings[assetType].length;

                                                return (
                                                    <div key={assetType} className="border border-gray-200 rounded-xl overflow-hidden bg-white">
                                                        <button
                                                            onClick={() => toggleSection(assetType)}
                                                            className={`w-full px-3 py-2 flex items-center justify-between transition-all duration-200 ${
                                                                isExpanded
                                                                    ? 'bg-gradient-to-r from-slate-50 to-blue-50/50'
                                                                    : 'bg-gray-50 hover:bg-gray-100'
                                                            }`}
                                                        >
                                                            <div className="flex items-center gap-2">
                                                                {isExpanded ? (
                                                                    <ChevronUpIcon className="h-4 w-4 text-blue-500" />
                                                                ) : (
                                                                    <ChevronDownIcon className="h-4 w-4 text-gray-600" />
                                                                )}
                                                                <span className="text-sm font-semibold text-gray-800">{assetType}</span>
                                                                <span className="text-xs text-gray-500">({holdingCount})</span>
                                                            </div>
                                                            <span className="text-sm font-medium text-gray-700">{totalWeight.toFixed(1)}%</span>
                                                        </button>
                                                        {isExpanded && (
                                                            <div className="border-t border-gray-200">
                                                                <table className="min-w-full bg-white text-xs">
                                                                    <thead className="bg-gray-50/80">
                                                                        <tr>
                                                                            <th className="py-1.5 px-2.5 text-left font-medium text-gray-600 text-xs">
                                                                                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.holdings`)}
                                                                            </th>
                                                                            <th className="py-1.5 px-2.5 text-right font-medium text-gray-600 text-xs">
                                                                                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.weight`)}
                                                                            </th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody className="divide-y divide-gray-100">
                                                                        {groupedHoldings[assetType].map((holding, index) => (
                                                                            <tr key={index} className="hover:bg-blue-50/50 transition-colors duration-150">
                                                                                <td className="py-1.5 px-2.5 text-gray-800 text-xs">{holding.name}</td>
                                                                                <td className="py-1.5 px-2.5 text-right font-medium text-gray-700 text-xs">{holding.weight}%</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })}
                                    </div>
                                ) : (
                                    // Simple table without grouping
                                    <table className="min-w-full bg-white border border-gray-200 rounded-xl text-xs">
                                        <thead className="bg-gray-50/80">
                                            <tr>
                                                <th className="py-2 px-3 text-left font-semibold text-gray-700 border-b text-xs">
                                                    {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.holdings`)}
                                                </th>
                                                <th className="py-2 px-3 text-right font-semibold text-gray-700 border-b text-xs">
                                                    {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.holdingsTable.weight`)}
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-gray-200">
                                            {sortedHoldings.map((holding, index) => (
                                                <tr key={index} className="hover:bg-blue-50/50 transition-colors duration-150">
                                                    <td className="py-1.5 px-3 text-gray-800 text-xs">{holding.name}</td>
                                                    <td className="py-1.5 px-3 text-right font-medium text-xs">{holding.weight}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>

                        {/* Asset Exposure Pie Chart - Right Side */}
                        <div>
                            <h4 className="text-xl font-semibold text-gray-800 mb-4">
                                {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.assetExposure`)}
                            </h4>
                            <div className="h-96 flex items-center justify-center">
                                <ResponsiveContainer width="100%" height="100%">
                                    <PieChart>
                                        <Pie
                                            data={assetExposureData}
                                            cx="50%"
                                            cy="50%"
                                            labelLine={true}
                                            label={({ name, value }) => {
                                                return value > 0 ? `${name}: ${value}%` : '';
                                            }}
                                            outerRadius={100}
                                            fill="#8884d8"
                                            dataKey="value"
                                            minAngle={5}
                                        >
                                            {assetExposureData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip formatter={(value) => `${value}%`} />
                                        <Legend
                                            verticalAlign="bottom"
                                            height={36}
                                            formatter={(value, entry) => {
                                                const item = assetExposureData.find(d => d.name === value);
                                                return `${value} (${item ? item.value : 0}%)`;
                                            }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    </div>
                </div>
            </Reveal>

            {/* Factsheets Section */}
            <Reveal delay={200}>
                <div>
                    <h4 className="text-xl font-semibold text-gray-800 mb-6">
                        {t(`modelPortfolioService.cogentModelPortfolios.${portfolioKey}.factsheets`)}
                    </h4>
                    {loadingFactsheets ? (
                        <div className="text-center py-8 text-gray-500">Loading factsheets...</div>
                    ) : factsheets.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">No factsheets available.</div>
                    ) : (
                        <div className="space-y-3">
                            {factsheets.map((file, index) => (
                                <a
                                    key={index}
                                    href={file.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center justify-between p-4 bg-gradient-to-r from-slate-50 to-blue-50/30 hover:from-blue-50 hover:to-blue-100/50 rounded-xl border border-gray-100 transition-all duration-200"
                                >
                                    <div className="flex items-center gap-3">
                                        <DocumentArrowDownIcon className="h-6 w-6 text-blue-600" />
                                        <div>
                                            <div className="font-medium text-gray-900">{file.name}</div>
                                            <div className="text-sm text-gray-500">{file.month} {file.year}</div>
                                        </div>
                                    </div>
                                    <span className="text-blue-600 hover:text-blue-800 text-sm font-semibold">Download</span>
                                </a>
                            ))}
                        </div>
                    )}
                </div>
            </Reveal>
        </div>
    );
};

// --- Main Component ---
const ManagedPortfolioService = () => {
    const { t } = useTranslation();
    const [factsheets, setFactsheets] = useState([]);
    const [loadingFactsheets, setLoadingFactsheets] = useState(true);
    const [activeTab, setActiveTab] = useState('balanced'); // Default to 'balanced'
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    // Load factsheets from backend
    useEffect(() => {
        const fetchFactsheets = async () => {
            setLoadingFactsheets(true);
            try {
                const response = await api.get(`/api/managed-portfolio-factsheets`);
                setFactsheets(response.data.files || []);
            } catch (error) {
                console.error('Failed to fetch factsheets:', error);
                setFactsheets([]);
            } finally {
                setLoadingFactsheets(false);
            }
        };

        fetchFactsheets();
    }, []);

    // Get current portfolio data based on active tab
    const getCurrentPortfolioData = () => {
        if (activeTab === 'balanced') return balanceGlobalData;
        if (activeTab === 'cautious') return cautiousGlobalData;
        if (activeTab === 'wealthBuilder') return wealthGlobalData;
        if (activeTab === 'shariahBalanced') return shariahBalancedData;
        return tacticalGlobalData; // tacticalAllocation
    };

    const getCurrentPortfolioKey = () => {
        if (activeTab === 'balanced') return 'balancedInternational';
        if (activeTab === 'cautious') return 'cautiousInternational';
        if (activeTab === 'wealthBuilder') return 'wealthBuilderInternational';
        if (activeTab === 'shariahBalanced') return 'shariahBalancedInternational';
        return 'tacticalAllocationInternational';
    };

    // Close mobile menu when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (mobileMenuOpen && !event.target.closest('.mobile-menu-container')) {
                setMobileMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [mobileMenuOpen]);

    return (
        <div className="flex flex-col min-h-screen bg-white">
            <Helmet>
              <title>Managed Portfolio Service — Cogent Platform</title>
              <meta name="description" content="Discover Cogent Asset Management's professionally managed portfolios including Balance Global, Cautious Global, Wealth Global, Tactical Global, and Shariah Balanced strategies." />
              <link rel="canonical" href="https://cogentplatform.com/managed-portfolio-service" />
            </Helmet>
            <Navbar />
            <main className="flex-grow">
                {/* Header */}
                <PageHero title={t('modelPortfolioService.title')} />

                {/* Introduction Section */}
                <div className="bg-gradient-to-b from-white to-slate-50">
                    <Section>
                        <Reveal>
                            <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 prose prose-lg text-justify">
                                <SectionTitle>{t('modelPortfolioService.introduction.title')}</SectionTitle>
                                <p className="mt-6">
                                    {t('modelPortfolioService.introduction.paragraph1')}
                                </p>
                                <p>
                                    {t('modelPortfolioService.introduction.paragraph2')}
                                </p>
                            </div>
                        </Reveal>
                    </Section>
                </div>

                {/* Cogent Managed Portfolios Section */}
                <div className="bg-gradient-to-b from-slate-50 to-white">
                    <Section>
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            <Reveal>
                                <SectionTitle>{t('modelPortfolioService.cogentModelPortfolios.title')}</SectionTitle>
                            </Reveal>

                            {/* Tabs */}
                            <Reveal delay={100} className="relative z-20">
                                <div className="mt-8 flex justify-center">
                                    <div className="w-full max-w-6xl">
                                        {/* Mobile: Dropdown Menu */}
                                        <div className="lg:hidden relative mobile-menu-container">
                                            <button
                                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                                className="w-full flex items-center justify-between px-4 py-3 bg-white rounded-xl border border-gray-200 shadow-sm text-left font-semibold text-sm text-gray-900 hover:bg-gray-50 transition-colors"
                                            >
                                                <span>
                                                    {activeTab === 'cautious' && t('modelPortfolioService.cogentModelPortfolios.cautiousInternational.title')}
                                                    {activeTab === 'balanced' && t('modelPortfolioService.cogentModelPortfolios.balancedInternational.title')}
                                                    {activeTab === 'wealthBuilder' && t('modelPortfolioService.cogentModelPortfolios.wealthBuilderInternational.title')}
                                                    {activeTab === 'tacticalAllocation' && t('modelPortfolioService.cogentModelPortfolios.tacticalAllocationInternational.title')}
                                                    {activeTab === 'shariahBalanced' && t('modelPortfolioService.cogentModelPortfolios.shariahBalancedInternational.title')}
                                                </span>
                                                <ChevronDownIcon className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${mobileMenuOpen ? 'rotate-180' : ''}`} />
                                            </button>

                                            {mobileMenuOpen && (
                                                <div className="absolute z-50 w-full mt-2 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('cautious');
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors ${
                                                            activeTab === 'cautious'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('modelPortfolioService.cogentModelPortfolios.cautiousInternational.title')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('balanced');
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors border-t border-gray-200 ${
                                                            activeTab === 'balanced'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('modelPortfolioService.cogentModelPortfolios.balancedInternational.title')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('wealthBuilder');
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors border-t border-gray-200 ${
                                                            activeTab === 'wealthBuilder'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('modelPortfolioService.cogentModelPortfolios.wealthBuilderInternational.title')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('tacticalAllocation');
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors border-t border-gray-200 ${
                                                            activeTab === 'tacticalAllocation'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('modelPortfolioService.cogentModelPortfolios.tacticalAllocationInternational.title')}
                                                    </button>
                                                    <button
                                                        onClick={() => {
                                                            setActiveTab('shariahBalanced');
                                                            setMobileMenuOpen(false);
                                                        }}
                                                        className={`w-full px-4 py-3 text-left text-sm font-medium transition-colors border-t border-gray-200 ${
                                                            activeTab === 'shariahBalanced'
                                                                ? 'bg-blue-50 text-blue-600'
                                                                : 'text-gray-700 hover:bg-gray-50'
                                                        }`}
                                                    >
                                                        {t('modelPortfolioService.cogentModelPortfolios.shariahBalancedInternational.title')}
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        {/* Desktop: Wrapped flex tabs */}
                                        <div className="hidden lg:flex justify-center">
                                            <div className="inline-flex flex-wrap gap-1 rounded-xl bg-gray-100/80 p-1.5 shadow-sm border border-gray-200 justify-center backdrop-blur-sm">
                                                <button
                                                    onClick={() => setActiveTab('cautious')}
                                                    className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 ${
                                                        activeTab === 'cautious'
                                                            ? 'bg-white text-blue-600 shadow-md rounded-lg'
                                                            : 'text-gray-600 hover:text-gray-900 rounded-lg'
                                                    }`}
                                                >
                                                    {t('modelPortfolioService.cogentModelPortfolios.cautiousInternational.title')}
                                                </button>
                                                <button
                                                    onClick={() => setActiveTab('balanced')}
                                                    className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 ${
                                                        activeTab === 'balanced'
                                                            ? 'bg-white text-blue-600 shadow-md rounded-lg'
                                                            : 'text-gray-600 hover:text-gray-900 rounded-lg'
                                                    }`}
                                                >
                                                    {t('modelPortfolioService.cogentModelPortfolios.balancedInternational.title')}
                                                </button>
                                                <button
                                                    onClick={() => setActiveTab('wealthBuilder')}
                                                    className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 ${
                                                        activeTab === 'wealthBuilder'
                                                            ? 'bg-white text-blue-600 shadow-md rounded-lg'
                                                            : 'text-gray-600 hover:text-gray-900 rounded-lg'
                                                    }`}
                                                >
                                                    {t('modelPortfolioService.cogentModelPortfolios.wealthBuilderInternational.title')}
                                                </button>
                                                <button
                                                    onClick={() => setActiveTab('tacticalAllocation')}
                                                    className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 ${
                                                        activeTab === 'tacticalAllocation'
                                                            ? 'bg-white text-blue-600 shadow-md rounded-lg'
                                                            : 'text-gray-600 hover:text-gray-900 rounded-lg'
                                                    }`}
                                                >
                                                    {t('modelPortfolioService.cogentModelPortfolios.tacticalAllocationInternational.title')}
                                                </button>
                                                <button
                                                    onClick={() => setActiveTab('shariahBalanced')}
                                                    className={`px-4 py-2.5 font-semibold text-sm transition-all duration-200 ${
                                                        activeTab === 'shariahBalanced'
                                                            ? 'bg-white text-blue-600 shadow-md rounded-lg'
                                                            : 'text-gray-600 hover:text-gray-900 rounded-lg'
                                                    }`}
                                                >
                                                    {t('modelPortfolioService.cogentModelPortfolios.shariahBalancedInternational.title')}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>

                            {/* Portfolio Display */}
                            <Reveal delay={200}>
                                <PortfolioDisplay
                                    portfolioData={getCurrentPortfolioData()}
                                    portfolioKey={getCurrentPortfolioKey()}
                                    factsheets={factsheets}
                                    loadingFactsheets={loadingFactsheets}
                                    t={t}
                                />
                            </Reveal>
                        </div>
                    </Section>
                </div>

            </main>
            <Footer />
        </div>
    );
};

export default ManagedPortfolioService;
